import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Sun, BatteryCharging, ArrowRight, Upload, Sparkles, LineChart, ShieldCheck, Clock } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.pexels.com/photos/8783541/pexels-photo-8783541.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600";
const SCAN_URL = "/?src=meta";

const SCENARIOS = [
  {
    icon: Zap,
    tag: "Scenario Light",
    title: "Ottimizza le forniture attuali",
    desc: "Risparmio immediato, investimento zero. Rinegoziamo la sola materia energia/gas sui tuoi consumi reali.",
  },
  {
    icon: Sun,
    tag: "Scenario Standard",
    title: "Fotovoltaico su misura",
    desc: "Impianto dimensionato sui tuoi consumi. Abbatti i costi fissi per sempre e proteggiti dai rincari.",
    highlight: true,
  },
  {
    icon: BatteryCharging,
    tag: "Scenario Premium",
    title: "Fotovoltaico + Accumulo",
    desc: "La massima indipendenza energetica per la tua impresa: produci di giorno, consumi anche di notte.",
  },
];

const STEPS = [
  { icon: Upload, t: "Carica la bolletta", d: "PDF o foto/scansione. Ci bastano pochi secondi." },
  { icon: Sparkles, t: "L'IA analizza i consumi", d: "Lettura automatica di fasce, prezzi e potenza." },
  { icon: LineChart, t: "Ricevi 3 piani di risparmio", d: "Su misura, con ROI e riduzione CO₂. Ora." },
];

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5, delay: d },
});

export default function CampagnaB2B() {
  return (
    <div className="min-h-screen bg-[#0A1326]" data-testid="campagna-b2b-page">
      {/* Header */}
      <header className="sticky top-0 z-30 glass">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo height={40} />
          <Link to={SCAN_URL}>
            <Button className="h-10 rounded-xl bg-gold-500 hover:bg-gold-400 text-[#0A1A2F] font-semibold" data-testid="header-cta">
              Check-up gratuito <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Impianto fotovoltaico industriale" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1326] via-[#0A1326]/90 to-[#0A1326]/60" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-20 lg:py-28">
          <motion.div {...fade(0)} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-500" data-testid="hero-overline">
              <Clock className="h-3.5 w-3.5" /> Risparmio Energetico B2B · meno di 2 minuti
            </span>
            <h1 className="mt-4 font-heading font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              La tua azienda paga <span className="text-gold-400">più del necessario</span> per energia e gas?
            </h1>
            <p className="mt-5 text-slate-300 text-base sm:text-lg leading-relaxed max-w-xl">
              Carica una bolletta e la nostra Intelligenza Artificiale analizza i consumi reali,
              generando <b className="text-white">3 piani di risparmio su misura</b> per la tua impresa. Gratis e senza impegno.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to={SCAN_URL}>
                <Button className="h-14 px-8 rounded-xl bg-gold-500 hover:bg-gold-400 text-[#0A1A2F] font-bold text-base group glow-gold" data-testid="hero-cta">
                  Avvia lo scanner ora
                  <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <span className="inline-flex items-center gap-2 text-sm text-slate-400">
                <ShieldCheck className="h-4 w-4 text-gold-400" /> Nessun impegno · dati riservati
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Scenari */}
      <section className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
        <motion.div {...fade(0)} className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Tre percorsi di risparmio</span>
          <h2 className="mt-3 font-heading font-black tracking-tight text-white text-2xl sm:text-3xl lg:text-4xl">
            Scegli il ritmo della tua transizione energetica
          </h2>
        </motion.div>
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {SCENARIOS.map((s, i) => (
            <motion.div key={s.tag} {...fade(i * 0.1)}
              data-testid={`scenario-card-${i}`}
              className={`relative card-dark rounded-2xl p-7 ${s.highlight ? "border border-gold-500/50 bg-gold-500/[0.06] glow-gold" : "border border-white/10"}`}>
              {s.highlight && (
                <span className="absolute -top-3 left-7 text-[10px] font-bold uppercase tracking-wider text-[#0A1A2F] bg-gold-500 px-2.5 py-1 rounded-full">Consigliato</span>
              )}
              <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${s.highlight ? "bg-gold-500 text-[#0A1A2F]" : "bg-white/5 text-gold-400"}`}>
                <s.icon className="h-6 w-6" />
              </div>
              <p className="mt-5 text-xs font-bold uppercase tracking-[0.15em] text-amber-500">{s.tag}</p>
              <h3 className="mt-1 font-heading font-bold text-white text-xl">{s.title}</h3>
              <p className="mt-2 text-slate-400 text-sm leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Come funziona */}
      <section className="border-y border-white/10 bg-white/[0.02]">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-3 gap-8">
            {STEPS.map((st, i) => (
              <motion.div key={st.t} {...fade(i * 0.1)} className="flex items-start gap-4" data-testid={`step-${i}`}>
                <div className="h-11 w-11 shrink-0 rounded-xl bg-white/5 text-gold-400 flex items-center justify-center">
                  <st.icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-amber-500">Passo {i + 1}</p>
                  <h3 className="text-white font-semibold mt-0.5">{st.t}</h3>
                  <p className="text-slate-400 text-sm mt-1">{st.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA finale */}
      <section className="max-w-6xl mx-auto px-6 py-20 text-center">
        <motion.div {...fade(0)}>
          <h2 className="font-heading font-black tracking-tight text-white text-2xl sm:text-3xl lg:text-4xl max-w-2xl mx-auto">
            Scopri quanto puoi risparmiare, in 2 minuti
          </h2>
          <p className="mt-4 text-slate-400 max-w-xl mx-auto">
            Ricevi subito il tuo Energy Score e i 3 piani su misura. Un consulente ti contatterà per approfondire.
          </p>
          <Link to={SCAN_URL}>
            <Button className="mt-8 h-14 px-9 rounded-xl bg-gold-500 hover:bg-gold-400 text-[#0A1A2F] font-bold text-base group glow-gold" data-testid="final-cta">
              Fai il check-up energetico gratuito
              <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6" data-testid="campagna-footer">
        <div className="max-w-3xl mx-auto text-center space-y-3">
          <p className="text-[12px] text-slate-500 leading-relaxed">
            Per la gestione delle forniture di energia e gas, oltre alle successive valutazioni e modifiche di prodotto,
            all'audit predittivo e alle ulteriori soluzioni offerte, è necessario sottoscrivere il contratto di consulenza con la nostra società.
          </p>
          <p className="text-[11px] text-slate-600">
            DM Solutions Group SRLS · Via Panaro 85, Agliana (PT) · P.IVA/C.F. 02132120474 · info@dmsolutionsgroup.it
          </p>
        </div>
      </footer>
    </div>
  );
}
